import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { StickyNote, Plus, Trash2, Edit2, Check, X } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { format } from "date-fns";

export default function NotesPanel({ api, paperId, currentPage }) {
  const [notes, setNotes] = useState([]);
  const [allNotes, setAllNotes] = useState([]);
  const [newNote, setNewNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");
  const [viewMode, setViewMode] = useState("page"); // "page" or "all"

  const fetchNotes = async () => {
    try {
      const [pageRes, allRes] = await Promise.all([
        axios.get(`${api}/notes/${paperId}?page=${currentPage}`, { withCredentials: true }),
        axios.get(`${api}/notes/${paperId}`, { withCredentials: true }),
      ]);
      setNotes(pageRes.data);
      setAllNotes(allRes.data);
    } catch {
      // ignore
    }
  };

  useEffect(() => {
    if (paperId) fetchNotes();
  }, [api, paperId, currentPage]);

  const handleCreate = async () => {
    if (!newNote.trim()) return;
    setSaving(true);
    try {
      const res = await axios.post(`${api}/notes`, {
        paper_id: paperId,
        page_number: currentPage,
        text: newNote.trim(),
      }, { withCredentials: true });
      setNotes((prev) => [res.data, ...prev]);
      setAllNotes((prev) => [res.data, ...prev]);
      setNewNote("");
      toast.success("Note saved!");
    } catch {
      toast.error("Failed to save note");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (noteId) => {
    if (!editText.trim()) return;
    try {
      await axios.put(`${api}/notes/${noteId}`, { text: editText.trim() }, { withCredentials: true });
      const update = (list) => list.map((n) => n.id === noteId ? { ...n, text: editText.trim() } : n);
      setNotes(update);
      setAllNotes(update);
      setEditingId(null);
      toast.success("Note updated");
    } catch {
      toast.error("Failed to update");
    }
  };

  const handleDelete = async (noteId) => {
    try {
      await axios.delete(`${api}/notes/${noteId}`, { withCredentials: true });
      setNotes((prev) => prev.filter((n) => n.id !== noteId));
      setAllNotes((prev) => prev.filter((n) => n.id !== noteId));
      toast.success("Note deleted");
    } catch {
      toast.error("Failed to delete");
    }
  };

  const displayNotes = viewMode === "page" ? notes : allNotes;

  return (
    <div className="neo-card dark:bg-[#1E1E1E] dark:border-[#444] dark:shadow-[4px_4px_0px_0px_#444]" data-testid="notes-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <StickyNote className="w-5 h-5 text-neo-yellow" />
          <h3 className="font-heading font-bold dark:text-white">Notes</h3>
        </div>
        <div className="flex border-2 border-neo-border dark:border-[#444] rounded-lg overflow-hidden text-xs">
          <button
            onClick={() => setViewMode("page")}
            className={`px-2 py-1 font-bold ${viewMode === "page" ? "bg-neo-yellow text-neo-text" : "bg-white dark:bg-[#2A2A2A] dark:text-white"}`}
            data-testid="notes-page-view"
          >
            Page {currentPage + 1}
          </button>
          <button
            onClick={() => setViewMode("all")}
            className={`px-2 py-1 font-bold border-l-2 border-neo-border dark:border-[#444] ${viewMode === "all" ? "bg-neo-yellow text-neo-text" : "bg-white dark:bg-[#2A2A2A] dark:text-white"}`}
            data-testid="notes-all-view"
          >
            All ({allNotes.length})
          </button>
        </div>
      </div>

      {/* Add Note */}
      <div className="space-y-2 mb-3">
        <Textarea
          value={newNote}
          onChange={(e) => setNewNote(e.target.value)}
          placeholder={`Add a note for page ${currentPage + 1}...`}
          className="border-2 border-neo-border dark:border-[#444] dark:bg-[#2A2A2A] dark:text-white rounded-lg text-sm min-h-[60px] focus:ring-0 focus:border-[3px] focus:border-neo-yellow resize-none"
          rows={2}
          data-testid="note-input"
        />
        <button
          onClick={handleCreate}
          disabled={saving || !newNote.trim()}
          className={`neo-btn w-full text-sm flex items-center justify-center gap-1 ${
            saving || !newNote.trim() ? "bg-gray-200 text-gray-500 border-gray-300 shadow-none cursor-not-allowed" : "neo-btn-primary"
          }`}
          data-testid="save-note-btn"
        >
          <Plus className="w-3 h-3" />
          {saving ? "Saving..." : "Add Note"}
        </button>
      </div>

      {/* Notes List */}
      {displayNotes.length > 0 ? (
        <div className="space-y-2 max-h-[300px] overflow-y-auto pr-1">
          {displayNotes.map((note) => (
            <div
              key={note.id}
              className="p-2.5 rounded-lg border-2 border-neo-border/40 dark:border-[#444]/60 bg-neo-yellow/5 dark:bg-[#2A2A2A]"
              data-testid={`note-${note.id}`}
            >
              {editingId === note.id ? (
                <div className="space-y-1">
                  <Textarea
                    value={editText}
                    onChange={(e) => setEditText(e.target.value)}
                    className="border-2 border-neo-border dark:border-[#444] dark:bg-[#333] dark:text-white rounded text-sm min-h-[40px] resize-none"
                    rows={2}
                    autoFocus
                    data-testid="edit-note-input"
                  />
                  <div className="flex gap-1">
                    <button onClick={() => handleUpdate(note.id)} className="p-1 text-neo-green"><Check className="w-3.5 h-3.5" /></button>
                    <button onClick={() => setEditingId(null)} className="p-1 text-neo-red"><X className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
              ) : (
                <>
                  <p className="text-sm dark:text-gray-200 whitespace-pre-wrap">{note.text}</p>
                  <div className="flex items-center justify-between mt-1.5">
                    <span className="text-xs text-neo-text-secondary dark:text-gray-500">
                      {viewMode === "all" && `p.${(note.page_number || 0) + 1} · `}
                      {note.created_at ? format(new Date(note.created_at), "MMM d, h:mm a") : ""}
                    </span>
                    <div className="flex items-center gap-0.5">
                      <button
                        onClick={() => { setEditingId(note.id); setEditText(note.text); }}
                        className="p-0.5 text-neo-text-secondary hover:text-neo-text dark:hover:text-white"
                        data-testid={`edit-note-${note.id}`}
                      >
                        <Edit2 className="w-3 h-3" />
                      </button>
                      <button
                        onClick={() => handleDelete(note.id)}
                        className="p-0.5 text-neo-text-secondary dark:text-gray-400 hover:text-neo-red"
                        data-testid={`delete-note-${note.id}`}
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      ) : (
        <p className="text-xs text-neo-text-secondary dark:text-gray-500 text-center py-2" data-testid="no-notes">
          {viewMode === "page" ? `No notes on page ${currentPage + 1}` : "No notes yet for this paper"}
        </p>
      )}
    </div>
  );
}
